<template>
    <SimpleModal v-if="showModal" :show-close-button="false" @close="$emit('close')">
        <template v-slot:header>
            <h2>We're sorry to hear you'd like to delete your account.</h2>
        </template>
        <template v-slot:body>
            <h4>Please let us know what we can improve by filling out the form below.</h4>
            <p>
                <strong>Why are you deleting your account?</strong>
            </p>
            <div class="form-group">
                <div v-for="(reason, key) in user.delete_account_reasons" :key="key">
                    <label>
                        <input
                            type="radio"
                            name="delete_account_reason"
                            ref="reason"
                            :value="key"
                            v-model="formData.delete_account_reason"
                            v-validate.persist="'required'"
                            data-vv-as="delete account reason"
                            @change="onChangeReason" />
                        {{ reason }}
                    </label>
                </div>
                <div class="form-error">{{ errors.first('delete_account_reason') }}</div>
            </div>
            <div class="form-group" v-if="showReasonDescription">
                <textarea
                    name="delete_account_reason_description"
                    class="form-control"
                    :class="{ 'has-error': errors.first('delete_account_reason_description') }"
                    v-model="formData.delete_account_reason_description"
                    v-validate.persist="`max:150|required_if:reason,${user.delete_account_reasons.length - 1}`"
                    data-vv-as="reason"></textarea>
                <div class="form-error">{{ errors.first('delete_account_reason_description') }}</div>
            </div>
            <div class="form-group" v-if="!user.is_company && user.login_types.includes('password')">
                <label class="form-label" required>Please enter your password</label>
                <input
                    class="form-control"
                    :class="{ 'has-error': errors.first('password') }"
                    ref="password"
                    type="password"
                    name="password"
                    v-model="formData.password"
                    v-validate.persist="'required'"
                    data-vv-as="password" />
                <span class="form-error">{{ errors.first('password') }}</span>
            </div>
            <div class="form-group" v-else-if="!user.is_company">
                <label class="form-label" required>Please write <i>‘permanently delete‘</i></label>
                <input
                    class="form-control"
                    :class="{ 'has-error': errors.first('confirm_delete') }"
                    ref="confirm_delete"
                    type="text"
                    name="confirm_delete"
                    v-model="formData.confirm_delete"
                    v-validate.persist="'required'"
                    data-vv-as="delete confirmation" />
                <span class="form-error">{{ errors.first('confirm_delete') }}</span>
            </div>
            <div class="form-group text-secondary" v-if="!user.is_company">
                <p>
                    When you press the <span>‘Permanently delete my account’</span> button below, your ads, messages and other data will be removed
                    permanently and <strong>WILL NOT BE RECOVERABLE</strong>
                </p>
            </div>
            <div class="form-group text-secondary" v-else>
                <p>
                    By requesting for your account to be permanently deleted, your ads, messages and
                    other data will be removed permanently and <strong>WILL NOT BE RECOVERABLE</strong>
                </p>
                <p>All your agents and their adverts will also be deleted.</p>
            </div>
        </template>
        <template v-slot:footer>
            <div class="d-flex justify-content-between">
                <button type="button" class="btn btn--transparent-outlined" :disabled="isLoading" @click.prevent="onCancel">Cancel</button>
                <button
                    v-if="user.is_company"
                    type="button"
                    class="btn btn--secondary"
                    :disabled="isLoading"
                    @click.prevent="onRequestAccountDeletion"
                >
                    Request account deletion
                </button>
                <button
                    v-else
                    type="button"
                    class="btn btn--secondary"
                    :disabled="isLoading"
                    @click.prevent="onDelete"
                >
                    Permanently delete my account
                </button>
            </div>
        </template>
    </SimpleModal>
</template>

<script>
import SimpleModal from '@components/partials/modal/SimpleModal.vue';
import { redirectTo } from '@helpers/url-handler';
import { mapState } from 'vuex';

export default {
    $_veeValidate: {
        validator: 'new', // give me my own validator scope.
    },
    components: {
        SimpleModal,
    },
    props: {
        showModal: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            formData: {
                delete_account_reason: '',
                delete_account_reason_description: '',
                password: '',
                confirm_delete: '',
            },
            showReasonDescription: false,
            isLoading: false,
        };
    },
    updated() {
        if (this.showModal) {
            this.formData = {
                delete_account_reason: '',
                delete_account_reason_description: '',
                password: '',
                confirm_delete: '',
            };
            this.showReasonDescription = false;
        }
        this.$validator.reset();
    },
    computed: {
        ...mapState('user', ['user']),
    },
    methods: {
        onDelete() {
            this.$validator.validate().then((valid) => {
                if (valid) {
                    this.isLoading = true;
                    this.axios
                        .delete('/api/user/delete', { data: this.formData })
                        .then(() => {
                            window.showSuccess('Your account has been deleted.', '', () => {
                                this.doLogout();
                                redirectTo('/');
                            });
                        })
                        .finally(() => {
                            this.isLoading = false;
                        });
                }
            });
        },
        onRequestAccountDeletion() {
            this.$validator.validate().then((valid) => {
                if (valid) {
                    this.isLoading = true;
                    this.axios
                        .delete('/api/user/request-account-deletion', { data: this.formData })
                        .then(() => {
                            window.showSuccess('', 'Your request has been sent and will be processed soon.', () => {
                                this.doLogout();
                                redirectTo('/');
                            });
                        })
                        .finally(() => {
                            this.isLoading = false;
                        });
                }
            });
        },
        onCancel() {
            this.$emit('close');
        },
        onChangeReason(event) {
            this.showReasonDescription = parseFloat(event.target.value) === this.user.delete_account_reasons.length - 1;
            if (!this.showReasonDescription) {
                this.formData.delete_account_reason_description = '';
            }
        },
    },
};
</script>
